import { useEffect, useMemo, useState } from 'react'
import type { AppendixConsistencyReport, CodeItemGroup } from '../types/codeIndex'
import { buildCodeItemsPublishPayload, previewAppendixConsistency } from '../services/codeIndexService'

// The server owns the comparison. Preview requests never save the editor or its report.
export function useLiveAppendixConsistency(
  codeIndexId: string | undefined, projectId: string, groups: CodeItemGroup[],
  dirty: boolean, savedReport: AppendixConsistencyReport | null, enabled: boolean
) {
  const payload = useMemo(() => JSON.stringify(buildCodeItemsPublishPayload(groups)), [groups])
  const [preview, setPreview] = useState<{
    key: string; report: AppendixConsistencyReport | null; error?: boolean
  } | null>(null)
  const key = JSON.stringify([codeIndexId, projectId, payload, savedReport?.checkedAt])
  const active = enabled && dirty && Boolean(codeIndexId && projectId)

  useEffect(() => {
    if (!active || !codeIndexId) return
    const controller = new AbortController()
    let current = true
    const timer = setTimeout(() => {
      previewAppendixConsistency(codeIndexId, projectId, JSON.parse(payload), controller.signal)
        .then(report => { if (current) setPreview({ key, report }) })
        .catch(() => { if (current) setPreview({ key, report: null, error: true }) })
    }, 400)
    return () => { current = false; clearTimeout(timer); controller.abort() }
  }, [active, codeIndexId, projectId, payload, key])

  const latest = preview?.key === key ? preview : null
  return {
    report: active ? latest?.report ?? null : savedReport,
    message: !active ? undefined : !latest
      ? 'Checking combinations…'
      : latest.error ? 'Could not check combinations. Save changes to retry.' : undefined,
  }
}
